import React, { useState, useEffect } from 'react';
import { notification, Button, Space, Typography } from 'antd';
import { ExclamationCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useAuth } from './AuthContext';
import { logger } from '../../../utils/logger';

const AutoLogoutNotification = () => {
  const [notificationKey, setNotificationKey] = useState(null);
  const { isAuthenticated, logout } = useAuth();
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    if (!isAuthenticated) return;

    const formatTimeLeft = (minutes) => {
      if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}小時${mins}分鐘`;
      }
      return `${minutes}分鐘`;
    };

    const handleExtendSession = async () => {
      try {
        // 嘗試刷新令牌來延長會話
        const response = await fetch('/api/users/refresh-token', {
          method: 'POST',
          credentials: 'include'
        });
        
        if (response.ok) {
          // 更新登入時間
          localStorage.setItem('loginTime', Date.now().toString());
          if (notificationKey) {
            api.destroy(notificationKey);
            setNotificationKey(null);
          }
          
          api.success({
            message: '會話已延長',
            description: '您的登入會話已成功延長7天',
            placement: 'topRight',
            duration: 3,
          });
          
          logger.log('會話已延長');
        } else {
          throw new Error('無法延長會話');
        }
      } catch (error) {
        logger.error('延長會話失敗:', error);
        
        api.error({
          message: '延長會話失敗',
          description: '無法延長會話，將自動登出',
          placement: 'topRight',
          duration: 3,
        });
        
        // 如果無法延長會話，執行登出
        logout();
      }
    };

    const handleLogout = () => {
      if (notificationKey) {
        api.destroy(notificationKey);
        setNotificationKey(null);
      }
      logout();
    };

    const showWarningNotification = (minutes) => {
      const key = `logout-warning-${Date.now()}`;
      setNotificationKey(key);
      
      api.warning({
        key,
        message: '會話即將過期',
        description: (
          <div>
            <Typography.Text>
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              您的登入會話即將在 <strong>{formatTimeLeft(minutes)}</strong> 後過期
            </Typography.Text>
            <br />
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              為了保護您的帳戶安全，系統將自動登出。您可以選擇延長會話或立即登出。
            </Typography.Text>
          </div>
        ),
        icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
        placement: 'topRight',
        duration: 0, // 不自動關閉
        btn: (
          <Space>
            <Button 
              size="small" 
              onClick={handleExtendSession}
              type="primary"
            >
              延長會話
            </Button>
            <Button 
              size="small" 
              onClick={handleLogout}
              danger
            >
              立即登出
            </Button>
          </Space>
        ),
      });
    };

    const updateWarningNotification = (minutes) => {
      if (notificationKey) {
        api.warning({
          key: notificationKey,
          message: '會話即將過期',
          description: (
            <div>
              <Typography.Text>
                <ClockCircleOutlined style={{ marginRight: 8 }} />
                您的登入會話即將在 <strong>{formatTimeLeft(minutes)}</strong> 後過期
              </Typography.Text>
              <br />
              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                為了保護您的帳戶安全，系統將自動登出。您可以選擇延長會話或立即登出。
              </Typography.Text>
            </div>
          ),
          icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
          placement: 'topRight',
          duration: 0,
          btn: (
            <Space>
              <Button 
                size="small" 
                onClick={handleExtendSession}
                type="primary"
              >
                延長會話
              </Button>
              <Button 
                size="small" 
                onClick={handleLogout}
                danger
              >
                立即登出
              </Button>
            </Space>
          ),
        });
      }
    };

    const checkExpiry = () => {
      const loginTime = localStorage.getItem('loginTime');
      if (!loginTime) return;

      const loginTimestamp = parseInt(loginTime);
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000; // 7天
      const currentTime = Date.now();
      const timeElapsed = currentTime - loginTimestamp;
      const timeRemaining = sevenDaysInMs - timeElapsed;

      // 如果剩餘時間少於1小時，顯示警告
      const oneHourInMs = 60 * 60 * 1000;
      if (timeRemaining > 0 && timeRemaining < oneHourInMs) {
        const minutes = Math.floor(timeRemaining / 1000 / 60);
        
        if (!notificationKey) {
          showWarningNotification(minutes);
        } else {
          // 更新現有通知
          updateWarningNotification(minutes);
        }
      } else if (timeRemaining <= 0) {
        // 時間已到，自動登出
        logger.log('登入時間已到，自動登出');
        if (notificationKey) {
          api.destroy(notificationKey);
          setNotificationKey(null);
        }
        logout();
      } else {
        // 清除警告
        if (notificationKey) {
          api.destroy(notificationKey);
          setNotificationKey(null);
        }
      }
    };

    // 每分鐘檢查一次
    const interval = setInterval(checkExpiry, 60 * 1000);
    
    // 立即檢查一次
    checkExpiry();

    return () => clearInterval(interval);
  }, [isAuthenticated, logout, api, notificationKey]);

  return contextHolder;
};

export default AutoLogoutNotification; 

